import type { Booster, SaveData } from '../meta/progress';

/**
 * Everything that can be bought. Prices here are only the fallback for the
 * web test mode – the real, localised price always comes from the App Store
 * or Google Play at runtime (DESIGN_SPEC §10).
 */
export type ProductId = 'coins_small' | 'coins_medium' | 'coins_large' | 'helpers' | 'toolkit' | 'starter';

export interface ProductDef {
  id: ProductId;
  /** product identifier in App Store Connect, Google Play and RevenueCat */
  storeId: string;
  title: string;
  coins: number;
  boosters: Partial<Record<Booster, number>>;
  cosmetic?: string;
  /** can only be bought once (restorable); everything else is consumable */
  oneTime: boolean;
  fallbackPrice: number;
}

export const PRODUCTS: ProductDef[] = [
  { id: 'coins_small', storeId: 'cafepaws.coins.500', title: '500 Coins', coins: 500, boosters: {}, oneTime: false, fallbackPrice: 0.99 },
  { id: 'coins_medium', storeId: 'cafepaws.coins.1200', title: '1,200 Coins', coins: 1200, boosters: {}, oneTime: false, fallbackPrice: 1.99 },
  { id: 'coins_large', storeId: 'cafepaws.coins.3000', title: '3,000 Coins', coins: 3000, boosters: {}, oneTime: false, fallbackPrice: 3.99 },
  { id: 'helpers', storeId: 'cafepaws.boosters.helpers', title: 'Handy Helpers', coins: 0, boosters: { hammer: 1, spoon: 1, moves5: 1 }, oneTime: false, fallbackPrice: 1.99 },
  { id: 'toolkit', storeId: 'cafepaws.boosters.toolkit', title: 'Café Toolkit', coins: 0, boosters: { hammer: 3, spoon: 3, moves5: 3 }, oneTime: false, fallbackPrice: 4.99 },
  { id: 'starter', storeId: 'cafepaws.starter', title: 'Starter Pack', coins: 500, boosters: { hammer: 1, spoon: 1, moves5: 1 }, cosmetic: 'bandana_paw', oneTime: true, fallbackPrice: 1.99 },
];

export const product = (id: ProductId) => PRODUCTS.find((p) => p.id === id)!;
export const productByStoreId = (storeId: string) => PRODUCTS.find((p) => p.storeId === storeId);

/** Rewarded videos */
export const AD_REWARDS = {
  /** out of moves: "Watch video for +3" (once per attempt) */
  continueMoves: 3,
  /** shop: free coins per video, a few times a day */
  shopCoins: 25,
  shopCoinsPerDay: 5,
} as const;

/**
 * Coins for +5 moves after running out (mockup 25): 100 the first time in an
 * attempt, 200 for every further continue in the same attempt.
 */
export const CONTINUE_COST = [100, 200] as const;
export const continueCost = (usedThisAttempt: number) => CONTINUE_COST[Math.min(usedThisAttempt, CONTINUE_COST.length - 1)];

/**
 * Credits a purchase to the save game. Idempotent per store transaction:
 * the same transaction (e.g. re-delivered after an app restart) is ignored.
 */
export function grantPurchase(prev: SaveData, id: ProductId, transactionId: string): { save: SaveData; granted: boolean } {
  if (prev.purchases.tx.includes(transactionId)) return { save: prev, granted: false };
  const p = product(id);
  if (p.oneTime && prev.purchases.starter) return { save: prev, granted: false };
  const s: SaveData = structuredClone(prev);
  s.purchases.tx.push(transactionId);
  s.coins += p.coins;
  for (const [b, n] of Object.entries(p.boosters) as [Booster, number][]) s.boosters[b] += n;
  if (p.oneTime) s.purchases.starter = true;
  if (p.cosmetic && !s.cosmetics.includes(p.cosmetic)) s.cosmetics.push(p.cosmetic);
  return { save: s, granted: true };
}

/**
 * "Restore purchases": only one-time products come back (the pack flag and
 * its cosmetic); coins and boosters are consumables and are not restored.
 */
export function restorePurchases(prev: SaveData, owned: ProductId[]): { save: SaveData; restored: ProductId[] } {
  const s: SaveData = structuredClone(prev);
  const restored: ProductId[] = [];
  for (const id of owned) {
    const p = product(id);
    if (!p.oneTime || s.purchases.starter) continue;
    s.purchases.starter = true;
    if (p.cosmetic && !s.cosmetics.includes(p.cosmetic)) s.cosmetics.push(p.cosmetic);
    restored.push(id);
  }
  return { save: s, restored };
}

/** The coin pack with the most coins per money – "Best Value" only when that is really true. */
export function bestValue(prices: Partial<Record<ProductId, number>>): ProductId | null {
  let best: ProductId | null = null;
  let bestRate = 0;
  for (const p of PRODUCTS.filter((x) => x.coins > 0 && !x.oneTime)) {
    const price = prices[p.id];
    if (!price) continue;
    const rate = p.coins / price;
    if (rate > bestRate * 1.05) {
      best = p.id;
      bestRate = rate;
    }
  }
  return best;
}

/** Rewarded video for shop coins still available today? */
export function adCoinsLeft(s: SaveData, today: string): number {
  return s.adCoins.day === today ? Math.max(0, AD_REWARDS.shopCoinsPerDay - s.adCoins.count) : AD_REWARDS.shopCoinsPerDay;
}

export function grantAdCoins(prev: SaveData, today: string): SaveData {
  const s: SaveData = structuredClone(prev);
  const count = s.adCoins.day === today ? s.adCoins.count : 0;
  s.adCoins = { day: today, count: count + 1 };
  s.coins += AD_REWARDS.shopCoins;
  return s;
}

/**
 * Starter offer: optional and gentle. First time right after the shop
 * opens (level 3), then at most once a day on café visits, at most 4 times
 * in total, never again after it was bought.
 */
export function shouldOfferStarter(s: SaveData, today: string, shopOpen: boolean): boolean {
  if (!shopOpen || s.purchases.starter) return false;
  if (s.starterOffer.shown >= 4) return false;
  return s.starterOffer.lastDay !== today;
}

export function markStarterShown(prev: SaveData, today: string): SaveData {
  return { ...prev, starterOffer: { shown: prev.starterOffer.shown + 1, lastDay: today } };
}

export const today = () => new Date().toISOString().slice(0, 10);
